import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import api from '../api';
import Tooltip from './Tooltip';
import { showConfirm, showAlert } from '../utils/ModalService';


interface Tenant {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone_number?: string;
  date_of_birth?: string;
}

export const Tenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [newTenant, setNewTenant] = useState<Partial<Tenant>>({});
  const [message, setMessage] = useState('');
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (err: any) {
      console.error('Error fetching tenants:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Error fetching tenants.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewTenant(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    try {
      await api.post('/tenants', newTenant);
      setMessage('Tenant added successfully!');
      setNewTenant({});
      fetchTenants();
    } catch (error: any) {
      console.error('Error adding tenant:', error.response?.data || error.message);
      setMessage(error.response?.data?.detail || 'Error adding tenant.');
    }
  };

  const handleUpdateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTenantId) return;
    setMessage('');
    try {
      await api.put(`/tenants/${editingTenantId}`, newTenant);
      setMessage('Tenant updated successfully!');
      setEditingTenantId(null);
      setNewTenant({});
      fetchTenants();
    } catch (error: any) {
      console.error('Error updating tenant:', error.response?.data || error.message);
      setMessage(error.response?.data?.detail || 'Error updating tenant.');
    }
  };

  const handleEditClick = (tenant: Tenant) => {
    setEditingTenantId(tenant.id);
    setNewTenant(tenant);
    setMessage('');
  };

  const handleDeleteTenant = async (tenantId: string) => {
    setMessage('');
    showConfirm('Confirm Action', 'Are you sure you want to delete this tenant?', async () => {
      try {
        await api.delete(`/tenants/${tenantId}`);
        setMessage('Tenant deleted successfully!');
        fetchTenants();
      } catch (error: any) {
        console.error('Error deleting tenant:', error.response?.data || error.message);
        setMessage(error.response?.data?.detail || 'Error deleting tenant.');
      }
    });
  };

  if (loading) {
    return <div className="container mx-auto p-4">{t("tenants.k1")}</div>;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-danger">{t("tenants.k2")} {error}</div>;
  }

  return (
    <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
      <div className="flex items-center mb-4">
        <h2 className="text-2xl font-bold text-text-secondary">{t("tenants.k3")}</h2>
        <Tooltip text="This page allows you to manage your tenants. You can add, edit, and delete tenant records. Keeping this information accurate is crucial for lease management and communication." />
      </div>

      {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}

      <h3 className="text-xl font-bold mb-4 text-text-secondary">{editingTenantId ? t("tenants.k4") : t("tenants.k5")}</h3>
      <form onSubmit={editingTenantId ? handleUpdateTenant : handleAddTenant} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
        <input type="text" name="first_name" placeholder={t("tenants.k6")} value={newTenant.first_name || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="text" name="last_name" placeholder={t("tenants.k7")} value={newTenant.last_name || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="email" name="email" placeholder={t("tenants.k8")} value={newTenant.email || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
        <input type="text" name="phone_number" placeholder={t("tenants.k9")} value={newTenant.phone_number || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
        <label htmlFor="date_of_birth" className="col-span-full text-text-secondary">{t("tenants.k10")}</label>
        <input type="date" id="date_of_birth" name="date_of_birth" value={newTenant.date_of_birth || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
        <button type="submit" className="col-span-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
          {editingTenantId ? t("tenants.k11") : t("tenants.k12")}
        </button>
        {editingTenantId && (
          <button type="button" onClick={() => { setEditingTenantId(null); setNewTenant({}); setMessage(''); }} className="col-span-full bg-transparent border border-border-primary text-text-primary px-4 py-2 rounded hover:bg-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-border-primary">
            {t("tenants.k13")} </button>
        )}
      </form>

      <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("tenants.k14")}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tenants.length === 0 ? (
          <p className="text-text-secondary">{t("tenants.k15")}</p>
        ) : (
          tenants.map((tenant) => (
            <div key={tenant.id} className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
              <h4 className="text-lg font-semibold mb-2">{tenant.first_name} {tenant.last_name}</h4>
              {tenant.email && <p className="text-text-secondary"><strong>{t("tenants.k16")}</strong> {tenant.email}</p>}
              {tenant.phone_number && <p className="text-text-secondary"><strong>{t("tenants.k17")}</strong> {tenant.phone_number}</p>}
              {tenant.date_of_birth && <p className="text-text-secondary"><strong>{t("tenants.k18")}</strong> {new Date(tenant.date_of_birth).toLocaleDateString('en-US')}</p>}
              <div className="mt-4">
                <button onClick={() => handleEditClick(tenant)} className="bg-success text-white px-4 py-2 rounded hover:bg-success-dark mr-2">{t("tenants.k19")}</button>
                <button onClick={() => handleDeleteTenant(tenant.id)} className="bg-danger text-white px-4 py-2 rounded hover:bg-red-700">{t("tenants.k20")}</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

